/**
 * Stake-level prayer lists — global, live, shared across meetings.
 *
 * Before this module existed, each meeting's `prayer_list` agenda slot
 * stored its own free-text `notes` string and a "Move to next" button
 * manually copied the list forward. This module replaces that with two
 * org-scoped lists ("Stake Presidency", "Stake Council") that every
 * prayer_list slot points at via `slot.prayerListKey`. Editing a list
 * updates every meeting that references it on the next render — no more
 * carry-forward step.
 *
 * Storage: organizations/{orgId}/data/prayerLists, value =
 *   { presidency: string, council: string }   (PrayerListsBlob)
 */

import {
  type AgendaSlot,
  normalizePrayerListsBlob,
  type PrayerListsBlob,
} from '@leadership/types'

// Re-export the pure helpers from the shared types package so existing
// imports through this module keep working. Pure helpers moved there in
// the global-prayer-lists refactor so mobile can share them.
export {
  addPrayerListPerson,
  applyPrayerListText,
  defaultPrayerListKeyForMeetingType,
  getPrayerListNames,
  getPrayerListText,
  normalizePrayerListsBlob,
  removePrayerListPerson,
  renamePrayerListPerson,
  resolveSlotPrayerListKey,
  togglePrayerListMembership,
} from '@leadership/types'

const STORAGE_KEY = 'prayerLists'

// ─── Read / write ──────────────────────────────────────────────────────────

/**
 * Read the prayer-lists blob. Returns the empty shape if the doc is
 * missing — callers should treat that as "no lists yet" and run the
 * one-shot seed (see seedPrayerListsFromMeetings) when they have the
 * meetings array on hand.
 */
export async function loadPrayerLists(db: {
  get: (key: string) => Promise<any>
}): Promise<PrayerListsBlob> {
  const raw = await db.get(STORAGE_KEY)
  return normalizePrayerListsBlob(raw)
}

/** Save the full blob. Same write semantics as any other org-scoped doc. */
export async function savePrayerLists(
  db: { set: (key: string, value: any) => Promise<any> },
  value: PrayerListsBlob,
): Promise<any> {
  return db.set(STORAGE_KEY, normalizePrayerListsBlob(value))
}

/** Subscribe to live updates. Mirrors the other org-scoped subscriptions. */
export function subscribePrayerLists(
  db: { subscribe: (key: string, cb: (value: any) => void) => () => void },
  callback: (value: PrayerListsBlob) => void,
): () => void {
  return db.subscribe(STORAGE_KEY, (raw) => callback(normalizePrayerListsBlob(raw)))
}

// ─── One-shot seed from existing meetings ──────────────────────────────────

interface SeedMeeting {
  agenda?: { slots?: AgendaSlot[] } | null
  date?: string
  // Meeting-type discriminator — see meetingType() in shared/lib/stakeMeetings.
  // Accepted directly here so this module stays free of that import.
  resolvedType: string
}

/**
 * Build a seed blob from the org's existing meetings. Picks the most
 * recent meeting (by `date`) whose prayer_list slot has non-empty
 * `notes` and uses that text as the seed for BOTH lists. Per product
 * spec: both lists start identical and users diverge them later from
 * the Settings card.
 */
export function seedPrayerListsFromMeetings(meetings: SeedMeeting[]): PrayerListsBlob {
  // Sort newest-first so the first match wins.
  const sorted = [...meetings].sort((a, b) => (b.date || '').localeCompare(a.date || ''))

  let bestText = ''
  for (const m of sorted) {
    const slots = m.agenda?.slots || []
    for (const s of slots) {
      if (s.kind === 'prayer_list' && typeof s.notes === 'string' && s.notes.trim()) {
        bestText = s.notes
        break
      }
    }
    if (bestText) break
  }

  // Round-trip through the normalizer so the seed text is split into
  // a fully-formed `people` array (one entry per name, both lists
  // checked) without duplicating that logic here.
  return normalizePrayerListsBlob({ presidency: bestText, council: bestText })
}
